import { useState, useMemo } from 'react';
import { Badge } from '@/components/ui/badge';
import { useRaces } from '@/hooks/useRaces';
import { useResults, useTeamResults, useResultsSummary } from '@/hooks/useResults';
import { useResultsActions } from './useResultsActions';
import ResultsManagerView from './resultsmanager.view';

const STATUS_MAP: Record<number, string> = { 0: 'Finished', 1: 'DNF', 2: 'DNS', 3: 'DSQ' };
const STATUS_COLORS: Record<number, string> = { 0: 'bg-green-100 text-green-800', 1: 'bg-red-100 text-red-800', 2: 'bg-yellow-100 text-yellow-800', 3: 'bg-orange-100 text-orange-800' };

interface ResultsManagerOwnProps {
  raceId?: string;
}

const ResultsManager: React.FC<ResultsManagerOwnProps> = ({ raceId: raceIdProp }) => {
  const isEmbedded = !!raceIdProp;
  const [selectedRaceIdState, setSelectedRaceIdState] = useState('');
  const selectedRaceId = isEmbedded ? raceIdProp : selectedRaceIdState;
  const [filters, setFilters] = useState({ raceTypeId: '', gender: '', ageGroup: '', search: '' });
  const [page, setPage] = useState(1);
  const [expandedRow, setExpandedRow] = useState<any>(null);
  const [tab, setTab] = useState('individual');

  const actions = useResultsActions(selectedRaceId);

  const { data: races = [] } = useRaces();
  const selectedRace = races.find((r: any) => r.id === selectedRaceId);
  const raceTypes = selectedRace?.raceTypes || [];

  const queryParams = tab === 'individual' ? {
    raceTypeId: filters.raceTypeId || undefined,
    gender: filters.gender || undefined,
    ageGroup: filters.ageGroup || undefined,
    search: filters.search || undefined,
    page,
    pageSize: 50,
  } : undefined;

  const { data: resultsData, isLoading: loadingIndividual } = useResults(
    tab === 'individual' ? selectedRaceId : null,
    queryParams,
  );
  const results = resultsData?.items || [];
  const total = resultsData?.total || 0;

  const { data: teams = [], isLoading: loadingTeams } = useTeamResults(
    tab === 'teams' ? selectedRaceId : null,
    { raceTypeId: filters.raceTypeId || undefined },
  );

  const { data: summary } = useResultsSummary(selectedRaceId);
  const loading = tab === 'individual' ? loadingIndividual : loadingTeams;

  const handleRaceChange = (raceId: string) => {
    setSelectedRaceIdState(raceId);
    actions.setImportResult(null);
    setPage(1);
    setFilters({ raceTypeId: '', gender: '', ageGroup: '', search: '' });
  };

  const totalPages = Math.ceil(total / 50);

  const individualColumns = useMemo(() => [
    { key: 'overallRank', label: '#', className: 'w-12', cellClassName: 'text-muted-foreground', render: (v: any) => v || '-' },
    { key: 'bibNumber', label: 'BIB', cellClassName: 'font-mono font-medium' },
    { key: 'fullName', label: 'Име', cellClassName: 'font-medium' },
    { key: 'gender', label: 'Пол', render: (v: any) => v === 'M' ? '♂' : v === 'F' ? '♀' : '-' },
    { key: 'gunTime', label: 'Gun Time', cellClassName: 'font-mono', render: (v: any) => v || '-' },
    { key: 'chipTime', label: 'Chip Time', cellClassName: 'font-mono', render: (v: any) => v || '-' },
    {
      key: 'genderRank', label: 'Ранг',
      render: (_: any, item: any) => (
        <div className="flex gap-1 flex-wrap">
          {item.genderRank && <Badge variant="secondary" className="text-xs">G:{item.genderRank}</Badge>}
          {item.ageGroupRank && <Badge variant="outline" className="text-xs">AG:{item.ageGroupRank}</Badge>}
        </div>
      ),
    },
    {
      key: 'status', label: 'Статус',
      render: (v: any) => (
        <Badge className={`text-xs ${STATUS_COLORS[v] || ''}`}>
          {STATUS_MAP[v]}
        </Badge>
      ),
    },
  ], []);

  return (
    <ResultsManagerView
      races={isEmbedded ? [] : races}
      selectedRaceId={selectedRaceId}
      handleRaceChange={handleRaceChange}
      summary={summary}
      tab={tab}
      setTab={setTab}
      loading={loading}
      raceTypes={raceTypes}
      filters={filters}
      setFilters={setFilters}
      setPage={setPage}
      individualColumns={individualColumns}
      results={results}
      expandedRow={expandedRow}
      setExpandedRow={setExpandedRow}
      page={page}
      totalPages={totalPages}
      total={total}
      teams={teams}
      fileInputRef={actions.fileInputRef}
      importing={actions.importing}
      importResult={actions.importResult}
      pasteValue={actions.pasteValue}
      setPasteValue={actions.setPasteValue}
      handleFileUpload={actions.handleFileUpload}
      handlePasteImport={actions.handlePasteImport}
      handleClearAll={actions.handleClearAll}
      embedded={isEmbedded}
    />
  );
};

export default ResultsManager;
